import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  ReportProblemRounded as ReportProblemRoundedIcon,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { HSpacer, RoutalSelect, RoutalSelectMultiSelectOption, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import React from 'react';
import { useIntl } from 'react-intl';

import Text from '../../../../react-components/Text';

export interface IMapField {
  key: string;
  text?: string | React.ReactElement;
  intlId?: string;
  intlValues?: Record<string, string | number | ((chunks: any) => React.ReactElement)>;
  category?: string;
  value?: string;
  validation?: { blank: number; correct: number; error: number };
}
interface ImportVehiclesDialogMapProps {
  headers: string[];
  fields: IMapField[];
  onChange: (field: IMapField, newColumnName: string | undefined) => void;
}

interface MappingSelectorProps {
  id: string;
  headers: string[];
  selected?: string;
  onChange?: (newValue: string | undefined) => void;
  validator?: (value: any) => boolean;
}
const MappingSelector = (props: MappingSelectorProps) => {
  const { id, headers, selected, onChange } = props;

  const createArrayActions = (actionId: string): RoutalSelectMultiSelectOption[] => {
    return [
      {
        key: `${actionId}_empty`,
        content: <Text style={{ cursor: `pointer` }}> - </Text>,
        onClick: () => onChange && onChange(undefined),
      },
      ...headers.map(
        (currValue, idx): RoutalSelectMultiSelectOption => ({
          key: `${actionId}-${idx}-${currValue}`,
          content: <Text style={{ cursor: `pointer` }}>{currValue}</Text>,
          onClick: () => onChange && onChange(currValue),
        })
      ),
    ];
  };

  return (
    <RoutalSelect
      id={`select_popover_${id}`}
      popoverProps={{
        options: createArrayActions(id),
      }}
      style={{
        minWidth: `260px`,
      }}
      containerStyle={{
        margin: `6px 0`,
      }}
    >
      <span style={{ width: `100%`, textAlign: `start` }}>
        {selected ? (
          <Text style={{ cursor: `pointer` }}>{selected}</Text>
        ) : (
          <Text style={{ cursor: `pointer`, color: `#AAAAAA` }} intlId="select_field" />
        )}
      </span>
    </RoutalSelect>
  );
};

type IMappingValidator = {
  result?: {
    blank?: number;
    errors?: number;
    correct?: number;
  };
};

const MappingValidator = (props: IMappingValidator) => {
  const { blank, errors } = props.result || {};
  return (
    <Grid container direction="row" alignItems="center" style={{ height: `100%` }}>
      {errors && errors > 0 ? (
        <RoutalTooltip
          id="mapping-validator-error"
          placement="right"
          title={
            <Text
              style={{ color: `white` }}
              intlId="properties.mapper.error.missing"
              intlValues={{ incorrectFields: errors }}
            />
          }
        >
          <CancelIcon style={{ color: `#D2190B` }} />
        </RoutalTooltip>
      ) : blank && blank > 0 ? (
        <RoutalTooltip
          id="mapping-validator-blank"
          placement="right"
          title={
            <Text
              style={{ color: `white` }}
              intlId="properties.mapper.error.blank"
              intlValues={{ blankFields: blank }}
            />
          }
        >
          <ReportProblemRoundedIcon style={{ color: `#FFC400` }} />
        </RoutalTooltip>
      ) : (!blank || blank === 0) && (!errors || errors === 0) ? (
        <CheckCircleIcon style={{ color: `#70D292` }} />
      ) : null}
    </Grid>
  );
};

const ImportVehiclesDialogMapper = ({ fields, headers, onChange }: ImportVehiclesDialogMapProps) => {
  const intl = useIntl();

  const handleMapChange = async (field: IMapField, newColumnName: string | undefined) => {
    onChange(field, newColumnName);
  };

  return (
    <Grid container direction="column" wrap="nowrap">
      {/* Label and location */}

      <Grid item container direction="column">
        <Text variant="body" intlId="plans.dialogs.routes_from_file.map.info" />

        <VSpacer medium />

        <Text
          variant="label"
          intlId="plans.dialogs.routes_from_file.mapped_columns"
          intlValues={{ num: fields.filter((field) => field.value).length, total: fields.length }}
        />
      </Grid>

      {fields.map((field: IMapField) => {
        let processedLabel;

        // Select the content of the button
        if (field.intlId) {
          const message = intl.formatMessage({ id: field.intlId }, field.intlValues);
          processedLabel = message;
        } else {
          processedLabel = field.text;
        }

        return (
          <Grid
            key={`${field.category}-${field.key}`}
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item container>
              <Grid item container alignItems="center">
                <Text variant="body-large" style={{ fontSize: `14px`, color: `#333940` }}>
                  {processedLabel}
                </Text>
              </Grid>
            </Grid>
            <Grid item container direction="row" wrap="nowrap">
              <Grid item style={{ width: `100%` }}>
                <MappingSelector
                  id={field.key}
                  onChange={(newValue) => handleMapChange(field, newValue)}
                  selected={field.value}
                  headers={headers}
                />
              </Grid>

              <HSpacer small />

              <Grid item>
                {field.validation ? <MappingValidator result={field.validation} /> : <Grid style={{ width: `25px` }} />}
              </Grid>
            </Grid>
          </Grid>
        );
      })}

      <VSpacer large />
    </Grid>
  );
};

export default ImportVehiclesDialogMapper;
